import type { ReactNode } from "react";
import { styled } from "@pigment-css/react";
import type { MeetingPrepMessagePart } from "@acto/genui-components-schema";
import { AnimateHeight } from "@acto/taskpane/field-sales-2/components/primitives/AnimateHeight";
import { TodoChecklistDone } from "./TodoChecklistCard";
import { isTodoChecklistRenderable, isTodoStory, useMeetingPrepTodosContext } from "./MeetingPrepTodosContext";

// The clip box must wear the story card's radius: mid-transition it crops the card, and a square
// clip edge would visibly strip the rounded corners while the height animates.
const RoundedClip = styled(AnimateHeight)(({ theme }) => ({
  borderRadius: theme.fieldSalesTwo.radius["44"],
}));

type StoryPart = Extract<MeetingPrepMessagePart, { type: "storyCard" }>;

/**
 * Owns the all-done presentation of a to-do story: once the checklist reports every own row
 * committed (and the grace period passed), the ENTIRE story card — archetype row, headline, actions
 * and all — glides into the confirmation capsule; after the close delay the story disappears for
 * the session. Only a story that is nothing but a keyed checklist swaps; mixed stories and unkeyed
 * checklists render their children untouched.
 */
export function TodoStoryFrame({ part, children }: { part: StoryPart; children: ReactNode }) {
  const state = useMeetingPrepTodosContext();

  const checklist = isTodoStory(part) ? part.talkingPoints[0] : undefined;
  const cardKey = checklist?.type === "todoChecklist" ? checklist.cardKey : undefined;
  if (!cardKey || checklist?.type !== "todoChecklist") return <>{children}</>;

  const swapsWholeStory = !state.delegatedOpenCardKeys.has(cardKey);
  if (state.dismissedCardKeys.has(cardKey) && swapsWholeStory) return null;

  // While the ids load, the story occupies the list at zero height and grows in when they arrive —
  // an abrupt pop would shove every story below it down in one frame.
  const pending = state.isPending;
  const renderable = isTodoChecklistRenderable(checklist, state);
  const confirming = state.confirmingCardKeys.has(cardKey) && swapsWholeStory;
  const phase = confirming ? "done" : pending ? "pending" : "story";
  if (!pending && !renderable && !confirming) return null;

  return (
    <RoundedClip transitionKey={phase}>
      {phase === "done" ? <TodoChecklistDone /> : phase === "story" ? children : null}
    </RoundedClip>
  );
}
